import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    ChartAccount,
    DividendsSummary,
    LedgerSettings,
    LocaleCode,
    Shareholder,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useChartOfAccounts,
    useDividendDeclarationsQuery,
    useLedgerSettingsQuery,
    useShareholdersQuery,
} from '@/api/queries';
import {
    resolveLocale,
    todayIso,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canReadDividends,
    deriveDividendsAccess,
    type DividendsAccess,
} from './dividendsAccess';
import {
    checkHoldings,
    computeTieOut,
    toDeclarationRows,
    toShareholderRows,
    toSummaryFigures,
    type DeclarationRow,
    type DividendsTieOut,
    type HoldingCheck,
    type ShareholderRow,
    type SummaryFigures,
} from './dividendsMeta';

/** Which drawer / dialog is open. Only one at a time. */
type OverlayState =
    | { kind: 'NONE' }
    | {
          kind: 'SHAREHOLDER';
          shareholder: Shareholder | null;
          instanceId: number;
      }
    | { kind: 'DECLARE' };

export type UseDividendsPageResult = {
    locale: LocaleCode;
    asOf: string;
    /** Below `full` the screen explains itself instead of rendering a register. */
    access: DividendsAccess;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    /** Null until the first load resolves. */
    figures: SummaryFigures | null;
    summary: DividendsSummary | null;
    /** Null while the chart of accounts is still loading. */
    tieOut: DividendsTieOut | null;
    /** Null while settings are still loading. */
    settings: LedgerSettings | null;
    rows: ShareholderRow[];
    holdings: HoldingCheck | null;
    accounts: ChartAccount[];
    declarationRows: DeclarationRow[];
    /** The history feed is still in flight — it must not claim "none yet". */
    isHistoryLoading: boolean;
    overlay: OverlayState;
    onOpenCreate: () => void;
    onOpenEdit: (shareholderId: string) => void;
    onOpenDeclare: () => void;
    onCloseOverlay: () => void;
};

/**
 * Drives the Profit & Dividends screen.
 *
 * It reads four independent feeds and joins them only where each is authoritative:
 * the register (its own accrued positions and the distributable-profit gate),
 * the declaration history, the **existing** chart-of-accounts / trial-balance
 * query (the general ledger, for the tie-out), and the ledger settings (so the
 * screen can state which regime and rates are in force). The register is not
 * allowed to certify itself, so the tie-out compares against the same balances
 * every other accounting screen reads.
 *
 * Before any of that it decides **whether it may read at all**. Every endpoint
 * behind this screen is OrgAdmin-with-a-password; for anyone else the queries can
 * only 403, so they are disabled and the screen explains itself. Discovering that
 * from a 403 would tell the user nothing they can act on.
 *
 * All derivation is pure and lives in `dividendsMeta`; this hook owns fetching and
 * which overlay is open.
 */
export const useDividendsPage = (): UseDividendsPageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // Read once per mount: the session cannot change without a navigation that
    // remounts this page.
    const access = React.useMemo(
        () => deriveDividendsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadDividends(access);

    // The equity position the gate reads is dated. Fixed for the session so the
    // figures don't shift under the user mid-decision; a date picker would be a
    // reasonable addition, and is noted in the contract's open questions.
    const [asOf] = React.useState(todayIso);
    const [overlay, setOverlay] = React.useState<OverlayState>({
        kind: 'NONE',
    });
    /** Bumped on each form open so a reopened drawer never inherits a draft. */
    const formInstanceRef = React.useRef(0);

    const registerQuery = useShareholdersQuery(asOf, canRead);
    const historyQuery = useDividendDeclarationsQuery(canRead);
    const settingsQuery = useLedgerSettingsQuery(canRead);
    // The chart is readable at BranchAdmin, but there is nothing to compare it
    // against here, so it is not fetched either.
    const chart = useChartOfAccounts({ locale, enabled: canRead });

    const { data: registerData, error: registerError } = unwrapStatement(
        registerQuery.data,
        registerQuery.error,
    );
    const { data: historyData } = unwrapStatement(
        historyQuery.data,
        historyQuery.error,
    );
    const { data: settingsData } = unwrapStatement(
        settingsQuery.data,
        settingsQuery.error,
    );

    const settings = settingsData?.settings ?? null;
    const shareholders = React.useMemo(
        () => registerData?.items ?? [],
        [registerData],
    );
    const accounts = chart.data ?? [];

    const figures = React.useMemo(
        () => (registerData ? toSummaryFigures(registerData.summary) : null),
        [registerData],
    );

    const tieOut = React.useMemo(
        () =>
            registerData
                ? computeTieOut(registerData.summary, chart.data)
                : null,
        [registerData, chart.data],
    );

    const rows = React.useMemo(
        () => toShareholderRows(shareholders, locale),
        [shareholders, locale],
    );

    const holdings = React.useMemo(
        () =>
            registerData
                ? checkHoldings(registerData.summary.totalHoldingBps)
                : null,
        [registerData],
    );

    const declarationRows = React.useMemo(
        () => toDeclarationRows(historyData?.items ?? []),
        [historyData],
    );

    const onOpenCreate = React.useCallback(() => {
        formInstanceRef.current += 1;
        setOverlay({
            kind: 'SHAREHOLDER',
            shareholder: null,
            instanceId: formInstanceRef.current,
        });
    }, []);

    const onOpenEdit = React.useCallback(
        (shareholderId: string) => {
            const shareholder =
                shareholders.find(
                    (candidate) => candidate.id === shareholderId,
                ) ?? null;
            if (!shareholder) {
                return;
            }
            formInstanceRef.current += 1;
            setOverlay({
                kind: 'SHAREHOLDER',
                shareholder,
                instanceId: formInstanceRef.current,
            });
        },
        [shareholders],
    );

    const onOpenDeclare = React.useCallback(() => {
        setOverlay({ kind: 'DECLARE' });
    }, []);

    const onCloseOverlay = React.useCallback(() => {
        setOverlay({ kind: 'NONE' });
    }, []);

    const onRetry = React.useCallback(() => {
        registerQuery.refetch();
        historyQuery.refetch();
        settingsQuery.refetch();
        chart.refetch();
    }, [registerQuery, historyQuery, settingsQuery, chart]);

    return {
        locale,
        asOf,
        access,
        isLoading: canRead && registerQuery.isLoading,
        error: registerError,
        onRetry,
        figures,
        summary: registerData?.summary ?? null,
        tieOut,
        settings,
        rows,
        holdings,
        accounts,
        declarationRows,
        isHistoryLoading: canRead && historyQuery.isLoading,
        overlay,
        onOpenCreate,
        onOpenEdit,
        onOpenDeclare,
        onCloseOverlay,
    };
};
